import React, { ReactNode, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { RbacAction, RbacModule } from "@/rbac/modules";

interface ReadOnlyScopeProps {
  module: RbacModule;
  /** Action that unlocks editing. Defaults to "edit". */
  action?: RbacAction;
  children: ReactNode;
  className?: string;
}

/**
 * Wraps a region of a page in a DOM-level disable when the user can view but
 * not edit. Applies `disabled` + `readOnly` to every form control inside and
 * blocks click handlers. Use around forms/editors so we don't need to thread
 * a prop through every nested input.
 */
export const ReadOnlyScope: React.FC<ReadOnlyScopeProps> = ({
  module,
  action = "edit",
  children,
  className,
}) => {
  const { can } = useAuth();
  const allowed = can(module, action);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (allowed) return;
    const root = ref.current;
    if (!root) return;

    const controls = root.querySelectorAll<HTMLElement>(
      "input, select, textarea, button",
    );
    controls.forEach((el) => {
      // Leave navigation/submit-less buttons that only read (data-permit-read)
      if (el.dataset.permitRead === "true") return;
      (el as HTMLInputElement).disabled = true;
      if ("readOnly" in el) {
        (el as HTMLInputElement).readOnly = true;
      }
      el.setAttribute("aria-disabled", "true");
    });
  });

  return (
    <div
      ref={ref}
      className={className}
      data-readonly-scope={allowed ? undefined : "true"}
      style={allowed ? undefined : { cursor: "default" }}
    >
      {children}
    </div>
  );
};
